import React, { useEffect, useState, useRef } from 'react';
import styles from './TablesPage.module.css';
import { api } from '../service/Axios';

// renderizar cada tabela com drag-to-scroll
const TableSection = ({ data, title, description }) => {
    const hasData = data && data.length > 0;
    const columns = hasData ? Object.keys(data[0]) : ["Informação"];

    // ref para permitir drag-to-scroll
    const containerRef = useRef(null);

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        let isDown = false;
        let startX;
        let scrollLeft;

        const handleMouseDown = (e) => {
            isDown = true;
            container.classList.add(styles.active);
            startX = e.pageX - container.offsetLeft;
            scrollLeft = container.scrollLeft;
        };

        const handleMouseLeave = () => {
            isDown = false;
            container.classList.remove(styles.active);
        };

        const handleMouseUp = () => {
            isDown = false;
            container.classList.remove(styles.active);
        };

        const handleMouseMove = (e) => {
            if (!isDown) return;
            e.preventDefault();
            const x = e.pageX - container.offsetLeft;
            const walk = (x - startX) * 1;
            container.scrollLeft = scrollLeft - walk;
        };

        container.addEventListener("mousedown", handleMouseDown);
        container.addEventListener("mouseleave", handleMouseLeave);
        container.addEventListener("mouseup", handleMouseUp);
        container.addEventListener("mousemove", handleMouseMove);

        return () => {
            container.removeEventListener("mousedown", handleMouseDown);
            container.removeEventListener("mouseleave", handleMouseLeave);
            container.removeEventListener("mouseup", handleMouseUp);
            container.removeEventListener("mousemove", handleMouseMove);
        };
    }, []);

    return (
        <div className={styles.tableSection}>
            <h2 className={styles.sectionTitle}>{title}</h2>
            {description && (
                <p className={styles.sectionDescription}>{description}</p>
            )}
            {/* contêiner com scroll normal + drag-to-scroll */}
            <div className={styles.tableContainer} ref={containerRef}>
                <table className={styles.ingredientTable}>
                    <thead>
                        <tr>
                            {columns.map((col, i) => (
                                <th
                                    key={i}
                                    className={col.toLowerCase() === "descrição" ? styles.descricaoCol : ""}
                                >
                                    {col}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {hasData ? (
                            data.map((row, i) => (
                                <tr
                                    key={i}
                                    className={i % 2 === 0 ? styles.evenRow : styles.oddRow}
                                >
                                    {columns.map((col, j) => (
                                        <td
                                            key={j}
                                            className={
                                                col.toLowerCase() === "descrição" ? styles.descricaoCol : ""
                                            }
                                        >
                                            {col === "Link Shop" && row[col] ? (
                                                <a
                                                    href={row[col]}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    <button className={styles.buyButton}>
                                                        Compre Aqui
                                                    </button>
                                                </a>
                                            ) : (
                                                row[col]
                                            )}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length} style={{ textAlign: "center" }}>
                                    Carregando dados...
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const TablesPage = () => {
    const bannerPersonalEmb = "https://promo.basf.com/campaign/EMB/banner-personal-emb.jpg";
    const [emolientes, setEmolientes] = useState([]);
    const [emulsionantes, setEmulsionantes] = useState([]);
    const [polimeros, setPolimeros] = useState([]);
    const [filtros, setFiltros] = useState([]);
    const [tensoativos, setTensoativos] = useState([]);
    const [perolizantes, setPerolizantes] = useState([]);
    const [opacificantes, setOpacificantes] = useState([]);
    const [estabilizantes, setEstabilizantes] = useState([]);
    const [outros, setOutros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [menuOpen, setMenuOpen] = useState(false);

    const API_URL = "tgxmydqs6n84z";

    // função para limpar dados inválidos
    const cleanData = (data) => {
        return data.filter(linha => {
            if (!linha) return false;
            return !Object.values(linha).every(v => (v || "").toString().trim() === "");
        });
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                // busca todas as abas em paralelo
                const endpoints = [
                    { name: "EMOLIENTES", setter: setEmolientes },
                    { name: "EMULSIONANTES_E_CERAS", setter: setEmulsionantes },
                    { name: "POLIMEROS", setter: setPolimeros },
                    { name: "FILTROS", setter: setFiltros },
                    { name: "TENSOATIVOS", setter: setTensoativos },
                    { name: "PEROLIZANTES", setter: setPerolizantes },
                    { name: "OPACIFICANTES", setter: setOpacificantes },
                    { name: "ESTABILIZANTES", setter: setEstabilizantes },
                    { name: "OUTROS", setter: setOutros }
                ];

                await Promise.all(
                    endpoints.map(async ({ name, setter }) => {
                        try {
                            const res = await api.get(`${API_URL}?sheet=${name}&headers=true`);
                            const dados = cleanData(res.data);
                            setter(dados);
                        } catch (err) {
                            console.error(`Erro ao carregar ${name}:`, err);
                            setter([]); // tentativa de evitar crash
                        }
                    })
                );
            } catch (err) {
                console.error("Erro geral ao carregar dados do SheetsDB:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return (
            <div className={styles.loaderWrapper}>
                <div className={styles.loader}></div>
                <div className={styles.loadText}>Conectando...</div>
            </div>
        );
    }

    // função para scroll + fechar o menu
    const handleNavClick = (id) => {
        const section = document.getElementById(id);
        if (section) {
            section.scrollIntoView({ behavior: "smooth" });
        }
        setMenuOpen(false); // fecha o menu após o click em alguma categoria
    };

    return (
        <div className={styles.pageContainer}>
            {/* header + banner */}
            <header className={styles.header}>
                <div className={styles.logo}>
                    <a href="https://shop.basf.com.br/pt-BR/">
                        <img src="https://promo.basf.com/campaign/basf.png" alt="BASF Logo" />
                    </a>
                </div>
                <h1 className={styles.headerTitle}>Conteúdo Técnico</h1>
            </header>

            <img
                src={bannerPersonalEmb}
                alt="Banner Personal Emp"
                className={styles.bannerImage}
            />

            {/* navegação */}
            <div className={styles.headerNav}>
                <button
                    className={`${styles.hamburger} ${menuOpen ? styles.hamburgerOpen : ""}`}
                    onClick={() => setMenuOpen(!menuOpen)}
                >
                    {menuOpen ? "✖" : "☰"}
                </button>

                <div className={`${styles.navContainer} ${menuOpen ? styles.showMenu : ""}`}>
                    <button className={styles.navButton} onClick={() => handleNavClick("emolientes")}>EMOLIENTES</button>
                    <button className={styles.navButton} onClick={() => handleNavClick("emulsionantes")}>EMULSIONANTES E CERAS</button>
                    <button className={styles.navButton} onClick={() => handleNavClick("polimeros")}>POLÍMEROS</button>
                    <button className={styles.navButton} onClick={() => handleNavClick("filtros")}>FILTROS</button>
                    <button className={styles.navButton} onClick={() => handleNavClick("tensoativos")}>TENSOATIVOS</button>
                    <button className={styles.navButton} onClick={() => handleNavClick("perolizantes")}>PEROLIZANTES</button>
                    <button className={styles.navButton} onClick={() => handleNavClick("opacificantes")}>OPACIFICANTES</button>
                    <button className={styles.navButton} onClick={() => handleNavClick("estabilizantes")}>ESTABILIZANTES</button>
                    <button className={styles.navButton} onClick={() => handleNavClick("outros")}>OUTROS</button>
                </div>
            </div>

            {/* <div className={styles.titles}>
                <h1 className={styles.mainTitle}>Ingredientes Cosméticos</h1>
                <p className={styles.subtitle}>
                    Guia completo com dados dos produtos!
                </p>
            </div> */}

            {/* Seções */}
            <div id="emolientes">
                <TableSection
                    data={emolientes}
                    title="EMOLIENTES"
                    // description="Emolientes versáteis que combinam desempenho funcional e sensorial, garantindo pele macia, hidratada."
                />
            </div>

            <div id="emulsionantes">
                <TableSection
                    data={emulsionantes}
                    title="Emulsionantes e Ceras"
                    // description="Ceras e emulsionantes versáteis que unem estabilidade, textura e brilho, elevando a performance e a experiência sensorial dos cosméticos."
                />
            </div>

            <div id="polimeros">
                <TableSection
                    data={polimeros}
                    title="Polímeros"
                    // description="Ingredientes responsáveis por estabilizar formulações e conferir textura adequada aos cosméticos."
                />
            </div>

            <div id="filtros">
                <TableSection
                    data={filtros}
                    title="Filtros"
                    // description="Ingredientes responsáveis por estabilizar formulações e conferir textura adequada aos cosméticos."
                />
            </div>

            <div id="tensoativos">
                <TableSection
                    data={tensoativos}
                    title="Tensoativos"
                    // description="Ingredientes responsáveis por estabilizar formulações e conferir textura adequada aos cosméticos."
                />
            </div>

            <div id="perolizantes">
                <TableSection
                    data={perolizantes}
                    title="Perolizantes"
                    // description="Ingredientes responsáveis por estabilizar formulações e conferir textura adequada aos cosméticos."
                />
            </div>

            <div id="opacificantes">
                <TableSection
                    data={opacificantes}
                    title="Opacificantes"
                    // description="Ingredientes responsáveis por estabilizar formulações e conferir textura adequada aos cosméticos."
                />
            </div>

            <div id="estabilizantes">
                <TableSection
                    data={estabilizantes}
                    title="Estabilizantes"
                    // description="Ingredientes responsáveis por estabilizar formulações e conferir textura adequada aos cosméticos."
                />
            </div>

            <div id="outros">
                <TableSection
                    data={outros}
                    title="Outros"
                    // description="Ingredientes responsáveis por estabilizar formulações e conferir textura adequada aos cosméticos."
                />
            </div>
        </div>
    );
};

export default TablesPage;
